import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'
import { parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoMarkupPrimitive } from './geckoMarkupPrimitive'

/**
 * An HTML anchor used to lay out this label primitive in 2D/3D space. For
 * example, if a "left" anchor is used, the left-center of the label will be
 * pinned to its coords on the asset.
 */
export enum GeckoMarkupLabelAnchor {
  Left = 'left',
  Right = 'right',
  Bottom = 'bottom',
  Top = 'top',
  Center = 'center',
}

/**
 * Markup icons are point-based primitives with a defined text property and
 * optionally-defined HTML anchor
 */
export class GeckoMarkupLabel extends GeckoMarkupPrimitive {
  //----------------------------------------------------------------------------

  public readonly kind = 'label'
  public readonly anchor: GeckoMarkupLabelAnchor | undefined
  public readonly text: string
  public readonly coords: GeckoComponentCoords

  //----------------------------------------------------------------------------

  private constructor(
    id: string,
    markupLayerID: string,
    json: Record<string, unknown>,
    markupBounds: GeckoComponentSpace
  ) {
    const x = parseNumber(json.x)
    const y = parseNumber(json.y)
    if (x === undefined || y === undefined)
      throw new Error(
        `Primitive "${id}" has unknown coords: ${JSON.stringify(json)}`
      )
    const coords = GeckoComponentCoords.CreateFromValues(
      markupBounds.componentID,
      x,
      markupBounds.x.unit,
      y,
      markupBounds.y.unit
    )
    super(
      id,
      markupLayerID,
      GeckoComponentRect.CreateFromCoords(coords, coords)
    )

    const text = parseString(json.text)
    if (text === undefined) throw new Error(`Primitive "${id}" is missing text`)

    const anchor = parseString(json.anchor)
    if (
      anchor !== undefined &&
      anchor !== 'left' &&
      anchor !== 'right' &&
      anchor !== 'bottom' &&
      anchor !== 'top' &&
      anchor !== 'center'
    )
      throw new Error(`Primitive "${id}" has unknown text anchor "${anchor}"`)

    this.text = text
    this.anchor = anchor as GeckoMarkupLabelAnchor
    this.coords = coords
  }

  //----------------------------------------------------------------------------

  public static CreateFromJSON(
    id: string,
    markupLayerID: string,
    json: Record<string, unknown>,
    markupBounds: GeckoComponentSpace
  ) {
    return new GeckoMarkupLabel(id, markupLayerID, json, markupBounds)
  }
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 *                                                    JSON-Type
 *           Copied from https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/3958603801/Markup+Layer+Schema
 * ---------------------------------------------------------------------------------------------------------------------
 */
export type GeckoMarkupLabelJSON = {
  kind: 'label'
  text: string
  x: number
  y: number
}
